import { useState, useEffect } from "react";
import { useComponentTranslation } from "@/services/i18n";
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import api from "@/services/api";
import useStore from "@/services/store";
import toast from "react-hot-toast";
import Modal from "@/components/modal";
import Loader from "@/components/loader";

export default function Templates() {
  const { t } = useComponentTranslation(translations);
  const { organization } = useStore();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    body: "",
  });

  // Load templates
  useEffect(() => {
    loadTemplates();
  }, [organization?._id]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const { ok, data } = await api.post("/template/search", { organization_id: organization?._id, organization_name: organization?.name });
      if (!ok) throw new Error("Failed to load templates");
      setTemplates(data);
    } catch (error) {
      console.error("Error loading templates:", error);
      toast.error(t("error_load_failed"));
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingTemplate(null);
    setFormData({
      name: "",
      subject: "",
      body: "",
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
    setFormData({ name: "", subject: "", body: "" });
    setSaving(false);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.body) {
      toast.error(t("required_fields"));
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        // Update existing template
        const { ok } = await api.put(`/template/${editingTemplate._id}`, formData);
        if (!ok) throw new Error("Failed to save template");
        toast.success(t("success_saved"));
      } else {
        // Create new template
        const { ok } = await api.post("/template", { ...formData, organization_id: organization?._id, organization_name: organization?.name });
        if (!ok) throw new Error("Failed to create template");
        toast.success(t("success_created"));
      }

      await loadTemplates();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(editingTemplate ? t("error_save_failed") : t("error_create_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!confirm(t("delete_confirm"))) return;

    try {
      const { ok } = await api.remove(`/template/${template._id}`);
      if (!ok) throw new Error("Failed to delete template");

      toast.success(t("success_deleted"));
      await loadTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(t("error_delete_failed"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t("title")}</h2>
            <p className="text-sm text-gray-600 mt-1">{t("description")}</p>
          </div>
          <button onClick={handleOpenCreateModal} className="btn btn-primary flex items-center gap-2">
            <FiPlus className="w-4 h-4" />
            {t("create_template")}
          </button>
        </div>

        {/* Templates List */}
        {templates.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <FiPlus className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t("no_templates")}</h3>
            <p className="text-sm text-gray-600 mb-6">{t("no_templates_desc")}</p>
            <button onClick={handleOpenCreateModal} className="btn btn-primary">
              {t("create_template")}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map((template) => (
              <div key={template._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900">{template.name}</h4>
                  <p className="text-sm text-gray-700 mt-1 font-medium">{template.subject}</p>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{template.body}</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleOpenEditModal(template)}
                    className="px-3 py-1.5 text-sm text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-1.5"
                    title={t("edit")}
                  >
                    <FiEdit2 className="w-4 h-4" />
                    {t("edit")}
                  </button>
                  <button
                    onClick={() => handleDelete(template)}
                    className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5"
                    title={t("delete")}
                  >
                    <FiTrash2 className="w-4 h-4" />
                    {t("delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} className="w-full max-w-4xl">
        <div className="p-8 flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{editingTemplate ? t("edit_template") : t("create_new_template")}</h2>
              {editingTemplate && <p className="text-sm text-gray-500 mt-1">{editingTemplate.name}</p>}
            </div>
          </div>

          {/* Template Name */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">{t("template_name")}</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input" placeholder={t("template_name")} />
          </div>

          {/* Template Subject */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">{t("template_subject")}</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="input"
              placeholder={t("template_subject")}
            />
          </div>

          {/* Template Body */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">{t("template_body")}</label>
            <textarea
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              rows={10}
              className="input py-2"
              placeholder={t("template_body")}
            />
            <p className="text-xs text-gray-500">{t("placeholders_help")}</p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button onClick={handleCloseModal} disabled={saving} className="btn btn-secondary">
              {t("cancel")}
            </button>
            <button onClick={handleSave} disabled={saving || !formData.name || !formData.subject || !formData.body} className="btn btn-primary min-w-[100px]">
              {saving ? <Loader size="small" /> : t("save")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const translations = {
  en: {
    title: "Email Templates",
    description: "Manage email templates for your recruitment process",
    create_template: "Create Template",
    no_templates: "No templates yet",
    no_templates_desc: "Create your first template to streamline your communication",
    template_name: "Template Name",
    template_subject: "Subject",
    template_body: "Body",
    edit: "Edit",
    delete: "Delete",
    create_new_template: "Create New Template",
    edit_template: "Edit Template",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving...",
    delete_confirm: "Are you sure you want to delete this template?",
    success_created: "Template created successfully!",
    success_saved: "Template saved successfully!",
    success_deleted: "Template deleted successfully!",
    error_create_failed: "Failed to create template",
    error_save_failed: "Failed to save template",
    error_delete_failed: "Failed to delete template",
    error_load_failed: "Failed to load templates",
    placeholders_help: "Available placeholders: {{candidate_name}}, {{job_title}}, {{company_name}}",
    required_fields: "All fields are required",
  },
  fr: {
    title: "Modèles d'e-mails",
    description: "Gérez des modèles d'e-mails pour votre processus de recrutement",
    create_template: "Créer un modèle",
    no_templates: "Aucun modèle pour le moment",
    no_templates_desc: "Créez votre premier modèle pour faciliter votre communication",
    template_name: "Nom du modèle",
    template_subject: "Sujet",
    template_body: "Corps",
    edit: "Modifier",
    delete: "Supprimer",
    create_new_template: "Créer un nouveau modèle",
    edit_template: "Modifier le modèle",
    cancel: "Annuler",
    save: "Enregistrer",
    saving: "Enregistrement...",
    delete_confirm: "Êtes-vous sûr de vouloir supprimer ce modèle ?",
    success_created: "Modèle créé avec succès !",
    success_saved: "Modèle enregistré avec succès !",
    success_deleted: "Modèle supprimé avec succès !",
    error_create_failed: "Échec de la création du modèle",
    error_save_failed: "Échec de l'enregistrement du modèle",
    error_delete_failed: "Échec de la suppression du modèle",
    error_load_failed: "Échec du chargement des modèles",
    placeholders_help: "Placeholders disponibles : {{candidate_name}}, {{job_title}}, {{company_name}}",
    required_fields: "Tous les champs sont requis",
  },
};
